const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  removeToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      // Handle empty responses or non-JSON content
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      const hasContent = contentLength && parseInt(contentLength) > 0;
      const isJson = contentType && contentType.includes('application/json');

      let data = {};

      if (hasContent || isJson) {
        try {
          const text = await response.text();
          if (text) {
            data = JSON.parse(text);
          }
        } catch (e) {
          console.warn('Failed to parse JSON response:', e);
          // Don't throw here, just treat as empty data if parsing fails
          // unless it's a 200 OK that expects JSON
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          this.removeToken();
          if (!url.includes('/auth/login')) {
            // Optional: window.location.reload(); 
          }
        }
        throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

const apiClient = new ApiClient();

export const api = {
  // Auth
  auth: {
    login: (credentials) => apiClient.post('/auth/login', credentials),
    register: (userData) => apiClient.post('/auth/register', userData),
    logout: () => apiClient.post('/auth/logout'),
    getCurrentUser: () => apiClient.get('/auth/me'),
  },

  // Users
  users: {
    getProfile: () => apiClient.get('/users/profile'),
    updateProfile: (data) => apiClient.put('/users/profile', data),
    getOrderSummary: () => apiClient.get('/users/orders/summary'),
    getAppointmentSummary: () => apiClient.get('/users/appointments/summary'),
  },

  // Services
  services: {
    getAll: () => apiClient.get('/services'),
    getById: (id) => apiClient.get(`/services/${id}`),
    create: (data) => apiClient.post('/services', data),
    update: (id, data) => apiClient.put(`/services/${id}`, data),
    delete: (id) => apiClient.delete(`/services/${id}`),
  },

  // Vets
  vets: {
    getAll: () => apiClient.get('/vets'),
    getById: (id) => apiClient.get(`/vets/${id}`),
    create: (data) => apiClient.post('/vets', data),
    update: (id, data) => apiClient.put(`/vets/${id}`, data),
    delete: (id) => apiClient.delete(`/vets/${id}`),
  },

  // Products
  products: {
    getAll: (params = {}) => {
      let endpoint = '/products';
      const searchParams = new URLSearchParams();
      if (params.forSale) searchParams.append('forSale', 'true');
      const queryString = searchParams.toString();
      if (queryString) endpoint += `?${queryString}`;
      return apiClient.get(endpoint);
    },
    getCategories: () => apiClient.get('/products/categories'),
    getById: (id) => apiClient.get(`/products/${id}`),
    create: (data) => apiClient.post('/products', data),
    update: (id, data) => apiClient.put(`/products/${id}`, data),
    updateStock: (id, data) => apiClient.patch(`/products/${id}/stock`, data),
    delete: (id) => apiClient.delete(`/products/${id}`),
    export: () => apiClient.get('/products/export'),
    bulk: (data) => apiClient.post('/products/bulk', data),
  },

  // Orders
  orders: {
    getAll: () => apiClient.get('/orders'),
    getById: (id) => apiClient.get(`/orders/${id}`),
    create: (data) => apiClient.post('/orders', data),
    updateStatus: (id, status) => apiClient.patch(`/orders/${id}/status`, { status }),
  },

  // Appointments
  appointments: {
    getAll: () => apiClient.get('/appointments'),
    getWaitlist: () => apiClient.get('/appointments/waitlist'),
    getById: (id) => apiClient.get(`/appointments/${id}`),
    create: (data) => apiClient.post('/appointments', data),
    updateStatus: (id, status) => apiClient.patch(`/appointments/${id}/status`, { status }),
    delete: (id) => apiClient.delete(`/appointments/${id}`),
  },

  // Analytics
  analytics: {
    getPerformance: () => apiClient.get('/analytics/performance'),
    getDistribution: () => apiClient.get('/analytics/distribution'),
  },

  // Patients
  patients: {
    getAll: () => apiClient.get('/patients'),
    getById: (id) => apiClient.get(`/patients/${id}`),
    create: (data) => apiClient.post('/patients', data),
    update: (id, data) => apiClient.put(`/patients/${id}`, data),
    addRecord: (id, data) => apiClient.post(`/patients/${id}/records`, data),
    delete: (id) => apiClient.delete(`/patients/${id}`),
  },

  // Upload
  upload: {
    uploadImage: (formData) => {
      return apiClient.request('/upload/image', {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set content-type for FormData
      });
    },
    deleteImage: (filename) => apiClient.delete(`/upload/image/${filename}`),
  },

  // Token Management
  setToken: (token) => apiClient.setToken(token),
  removeToken: () => apiClient.removeToken(),
};

export default api;